import React, { useState } from 'react';
import TopBackButton from '@/component/common/top-back-button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SFSwitch from '@/component/common/sf-switch';
import SFMenu from '@/component/my/sf-menu';
import SuccessDrawer from '@/component/common/success-drawer';
import { SFPalette } from '@/lib/color';
import { fontWeight, inter } from '@/theme';
import Stack from '@mui/material/Stack';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import SFFlagKoreaIcon from '@/component/icons/sf-flag-korea-icon';
import SFFlagKoreaCircleIcon from '@/component/icons/sf-flag-korea-circle-icon';
import SFFlagUsCircleIcon from '@/component/icons/sf-flag-us-circle-icon';

export default function WalletSecurityCenterPage() {
  const [anchorElLanguage, setAnchorElLanguage] = useState<HTMLButtonElement | null>(null);
  const [anchorElCurrency, setAnchorElCurrency] = useState<HTMLButtonElement | null>(null);
  const [language, setLanguage] = useState<string>('한국어');
  const [currency, setCurrency] = useState<string>('KRW');

  const handleOpenLanguagePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleOpenCurrencyPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElCurrency(event.currentTarget);
  };

  const handleCloseLanguagePopover = () => {
    setAnchorElLanguage(null);
  };

  const handleCloseCurrencyPopover = () => {
    setAnchorElCurrency(null);
  };

  const handleSelectLanguage = (newLanguage: string) => {
    setLanguage(newLanguage);
    handleCloseLanguagePopover();
  };

  const handleSelectCurrency = (newCurrency: string) => {
    setCurrency(newCurrency);
    handleCloseCurrencyPopover();
  };
  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '812px',
        height: '100vh',
        position: 'relative',
      }}
    >
      <TopBackButton
        title="설정"
        boxProps={{
          mb: 4,
        }}
      />
      <SFMenu.Menu>
        <Divider sx={{ mb: 1.5 }} />
        <SFMenu.BetweenItem
          leftContent={<Typography variant="subtitle2">서비스 알림</Typography>}
          rightContent={<SFSwitch />}
        />
        <Divider sx={{ my: 1.5 }} />
        <SFMenu.BetweenItem
          leftContent={<Typography variant="subtitle2">공지사항 알림</Typography>}
          rightContent={<SFSwitch />}
        />
        <Divider sx={{ my: 1.5 }} />
        <SFMenu.BetweenItem
          leftContent={<Typography variant="subtitle2">{`이벤트 알림 (마케팅 수신 동의)`}</Typography>}
          rightContent={<SFSwitch />}
        />
        <Divider sx={{ my: 1.5 }} />
        <Box>
          <Typography variant="subtitle2" py={1} color={SFPalette.grey2} fontWeight={fontWeight.Medium}>
            언어 설정
          </Typography>

          <SFMenu.BetweenItem
            onClick={handleOpenLanguagePopover as any}
            leftContent={<Typography variant="subtitle2">{`언어 설정 변경`}</Typography>}
            rightContent={
              <Stack direction="row" alignItems="center">
                <Typography variant="subtitle2">{language}</Typography>
                <ChevronRight
                  sx={{
                    color: SFPalette.lightGrey2,
                  }}
                />
              </Stack>
            }
          />
          <Popover
            open={Boolean(anchorElLanguage)}
            anchorEl={anchorElLanguage}
            onClose={handleCloseLanguagePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                width: '100%',
              },
            }}
          >
            <Stack>
              <IconButton
                onClick={() => handleSelectLanguage('한국어')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 2,
                  px: 2.5,
                }}
              >
                <Box mr={2}>
                  <SFFlagKoreaCircleIcon />
                </Box>
                <Typography variant="subtitle2" fontWeight={fontWeight.Bold} color="black">
                  한국어
                </Typography>
              </IconButton>
              <IconButton
                onClick={() => handleSelectLanguage('English')}
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  py: 2,
                  px: 2.5,
                }}
              >
                <Box mr={2}>
                  <SFFlagUsCircleIcon />
                </Box>
                <Typography variant="subtitle2" fontWeight={fontWeight.Bold} color="black">
                  English
                </Typography>
              </IconButton>
            </Stack>
          </Popover>
        </Box>
        <Divider sx={{ my: 1.5 }} />

        <Box>
          <Typography variant="subtitle2" py={1} color={SFPalette.grey2} fontWeight={fontWeight.Medium}>
            기준 통화 설정
          </Typography>
          <SFMenu.BetweenItem
            onClick={handleOpenCurrencyPopover as any}
            leftContent={<Typography variant="subtitle2">{`기준 통화 변경`}</Typography>}
            rightContent={
              <Stack direction="row" alignItems="center">
                <Typography variant="subtitle2">{currency}</Typography>
                <ChevronRight
                  sx={{
                    color: SFPalette.lightGrey2,
                  }}
                />
              </Stack>
            }
          />
          <Popover
            open={Boolean(anchorElCurrency)}
            anchorEl={anchorElCurrency}
            onClose={handleCloseCurrencyPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                width: '100%',
              },
            }}
          >
            <Stack>
              <IconButton
                onClick={() => handleSelectCurrency('KRW')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 2,
                  px: 2.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={fontWeight.Bold}
                  color={SFPalette.darkGrey2}
                  fontFamily={inter.style.fontFamily}
                  mr={1}
                >
                  ₩
                </Typography>
                <Typography variant="subtitle2" fontWeight={fontWeight.Bold} color="black">
                  KRW
                </Typography>
              </IconButton>
              <IconButton
                onClick={() => handleSelectCurrency('IDR')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 2,
                  px: 2.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={fontWeight.Bold}
                  color={SFPalette.darkGrey2}
                  fontFamily={inter.style.fontFamily}
                  mr={1}
                >
                  Rp
                </Typography>
                <Typography variant="subtitle2" fontWeight={fontWeight.Bold} color="black">
                  IDR
                </Typography>
              </IconButton>
            </Stack>
          </Popover>
        </Box>
        <Divider sx={{ my: 1.5 }} />
      </SFMenu.Menu>
    </Container>
  );
}
